import { getCdnURL } from '@/care/helpers';
import {
  ChannelOffer,
  SProduct,
  SProductList,
} from '@/generated/graphql/types';
import { generateUrl } from '@/productDiscovery/algolia/components/AlgoliaProductCard/AlgoliaProductCard.utils';
import { PromoTags } from '@/productDiscovery/CommercialAnimation/interfaces/tags';
import { getTags } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';
import {
  MabayaProduct,
  MabayaProductWithModelId,
} from '@/productDiscovery/SponsoredProducts/interfaces/MabayaProduct';
import {
  ProductWithSponsoredData,
  SponsoredProductsData,
} from '@/sellerAdsManagement/SponsoredProducts/interfaces/SponsoredProducts';

export const processSponsoredProducts = (
  sProductList: SProductList,
): SponsoredProductsData => {
  return {
    sponsoredProducts:
      sProductList.products && sProductList.products.length > 0
        ? sProductList.products.map((product: SProduct) => {
            return {
              adPayer: product.adPayer,
              adTrackingData: product.adTrackingData,
              cpc: product.mgr || 0,
              productId: parseInt(product.channelOffer!.productId, 10),
            };
          })
        : [],
    sponsoredProductPositions:
      sProductList.positions && sProductList.positions.length > 0
        ? sProductList.positions
        : [],
    isFirstMF: sProductList.isFirstMF,
  };
};

export const processSponsoredProductsDataByProduct = (
  products: SProduct[],
): MabayaProductWithModelId[] => {
  return products?.length > 0
    ? products.map((p) => {
        return {
          adPayer: p.adPayer,
          adTrackingData: p.adTrackingData,
          cpc: p.mgr || 0,
          productId: parseInt(p.channelOffer!.productId, 10),
          modelId: parseInt(p.channelOffer!.sku.modelId, 10),
        };
      })
    : [];
};

export const processOfferProductByProductId = (
  mabayaProduct: MabayaProduct,
  offer: ChannelOffer,
): ProductWithSponsoredData => {
  const seller = {
    id: parseInt(offer.seller?.contractId ?? '0', 10),
    name: offer.seller?.contractName ?? '',
  };
  const brand = {
    id: offer.sku?.masterProduct.brand?.publicId ?? 0,
    imageUrl: offer.sku?.masterProduct.brand?.logo ?? '',
    name: offer.sku?.masterProduct.brand?.title ?? '',
  };

  return {
    adPayer: mabayaProduct.adPayer,
    adTrackingData: mabayaProduct.adTrackingData,
    cpc: mabayaProduct.cpc || 0,
    productId: parseInt(offer.productId || '0', 10),
    modelId: parseInt(offer.sku?.modelId ?? '0', 10),
    articleId: parseInt(offer.sku?.masterProduct?.articleId ?? '0', 10),
    modelsCount: offer.variantsCount || 1,
    title: offer.sku?.title ?? '',
    productSlug: offer.sku?.masterProduct?.slug ?? '',
    imagePath:
      offer.sku?.images && offer.sku?.images.length > 0
        ? `${getCdnURL()}${offer.sku.images[0].thumbnailUrl || ''}`
        : '',
    price: {
      currentPrice: {
        amountWithVat: offer.pricing?.sellPrice?.amountVatIncluded ?? 0,
        amountWithoutVat: offer.pricing?.sellPrice?.amountVatExcluded ?? 0,
        currency: offer.pricing?.sellPrice?.currency,
      },
      retailPrice: {
        amountWithVat: offer.pricing?.retailPrice?.amountVatIncluded ?? 0,
        amountWithoutVat: offer.pricing?.retailPrice?.amountVatExcluded ?? 0,
        currency: offer.pricing?.retailPrice?.currency,
      },
    },
    rating: {
      average: offer.productRating?.average ?? 0,
      totalRatings: offer.productRating?.total ?? 0,
    },
    isFulfillment: offer.seller?.isMF ?? false,
    hasPromoLabel:
      getTags(
        offer.pricing?.sellPrice?.amountVatIncluded ?? 0,
        offer.pricing?.retailPrice?.amountVatIncluded ?? 0,
        offer.commercialAnimation?.tags ?? [],
      ).length > 0,
    promoTags: (offer.commercialAnimation?.tags as PromoTags[]) ?? [],
    url: generateUrl(
      {
        url: offer.sku?.masterProduct?.slug ?? '',
        modelId: parseInt(offer.sku?.modelId ?? '0', 10),
        articleId: parseInt(offer.sku?.masterProduct?.articleId ?? '0', 10),
        modelsCount: offer.variantsCount || 1,
        productId: offer.productId ?? '0',
      },
      false,
      true,
    ),
    seller,
    brand,
    advertiser: seller.name,
    sponsor: mabayaProduct.adPayer === 'SELLER' ? seller.name : brand.name,
    isSponsored: true,
    coupon: offer.coupon?.value ? offer.coupon.value : undefined,
  };
};

export const processOffersByProductIds = (
  mabayaProduct: MabayaProductWithModelId[],
  offers: ChannelOffer[],
): ProductWithSponsoredData[] => {
  const products: ProductWithSponsoredData[] = mabayaProduct.reduce(
    (previousValue, currentValue) => {
      const offer = offers.find(
        (o) => o?.productId === currentValue.productId.toString(),
      );

      if (offer) {
        previousValue.push(processOfferProductByProductId(currentValue, offer));
      }

      return previousValue;
    },
    [] as ProductWithSponsoredData[],
  );

  return products;
};
